import {
  SHIPPING_MODE_KEYS,
  resolvePreferredShippingMode,
} from "../constants/shipping";
import { estimateTransport } from "./pricing";
import { flattenVariants, parsePrice, productPrice } from "./productPrice";

/**
 * Marge d'une offre business, en pourcentage du prix de revient.
 *
 * Reprend, chiffre pour chiffre, le calcul du bloc « Lance ton Business » :
 * même quantité de simulation, même mode de transport présélectionné, même
 * arrondi de facturation. Un écart entre la pastille de la carte et le tableau
 * de la fiche se lirait comme une erreur de l'application.
 *
 * @param {object} product Produit brut de l'API (variantes chargées)
 * @param {{perKg:number, perCbm:number, airMeasureStep:number}} pricing Barème backend
 * @returns {number|null} Pourcentage entier, ou null si non calculable / marge nulle
 */
export const businessMarginPercent = (product, pricing) => {
  if (!product) return null;

  const isBusiness = product.is_business_offer === true || product.is_business_offer === 1;
  if (!isBusiness) return null;

  const localMarketPrice = parsePrice(product.local_market_price);
  if (localMarketPrice <= 0) return null;

  // Prix du gros, frais bancaires inclus — celui que la carte affiche.
  const unitPrice = productPrice(product, "normal");
  if (unitPrice <= 0) return null;

  const qty = Math.max(
    1,
    Number(product.business_simulation_quantity) || Number(product.moq) || 1
  );

  // Poids et volume de la combinaison la moins chère : c'est elle que le prix
  // affiché désigne, c'est donc la sienne qu'on achemine. La liste des produits
  // ne charge pas les variantes — l'API y sert la mesure au niveau du produit.
  const normals = flattenVariants(product, "normal");
  const cheapest = normals.slice().sort((a, b) => a.sale_price - b.sale_price)[0] || {};
  const weight = parsePrice(cheapest.weight) || parsePrice(product.unit_weight);
  const volume = parsePrice(cheapest.volume) || parsePrice(product.unit_volume);

  const modeKey = SHIPPING_MODE_KEYS[
    resolvePreferredShippingMode(product.shipping_modes, product.default_shipping_mode)
  ];

  // L'avion facture le poids, le bateau le volume : sans la mesure du mode
  // retenu, le transport vaudrait zéro et la marge annoncée dépasserait celle
  // du tableau de la fiche. Mieux vaut ne rien annoncer.
  if (modeKey === "aerien" ? weight <= 0 : volume <= 0) return null;

  const transport = estimateTransport(
    [{ weight, volume, quantity: qty }],
    modeKey,
    pricing
  );

  const costPerUnit = (unitPrice * qty + transport.cost) / qty;
  if (costPerUnit <= 0) return null;

  const margin = ((localMarketPrice - costPerUnit) / costPerUnit) * 100;
  if (!Number.isFinite(margin) || margin <= 0) return null;

  return Math.round(margin);
};

export default businessMarginPercent;
